const EUR = new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const abs = (n) => Math.abs(Number(n) || 0);
export const fmtMoney = (n) => { const s = EUR.format(abs(n)); return (Number(n) < 0 ? "−" : "") + s; };
export const fmtSignedMoney = (n) => { const v = Number(n) || 0; if (v === 0) return "€0"; return (v < 0 ? "−" : "+") + EUR.format(abs(v)); };
export const fmtPct = (n) => `${Math.round(Number(n) || 0)}%`;
export const fmtSignedPct = (n) => { const v = Math.round(Number(n) || 0); return (v < 0 ? "−" : "+") + `${Math.abs(v)}%`; };

export const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// netDelta — signed sum of driver cost deltas.
export const netDelta = (drivers = []) => (drivers || []).reduce((s, d) => s + (Number(d.deltaCost) || 0), 0);

// invNumber — "INV-{createdYear}-{id padded to 3}".
export const invNumber = (inv = {}) => {
  const year = inv.createdAt ? new Date(inv.createdAt).getFullYear() : new Date().getFullYear();
  const id = String(inv.id ?? 0).padStart(3, "0");
  return `INV-${year}-${id}`;
};

// deriveTitle — business title from top driver + net delta + period month.
export const deriveTitle = (inv = {}, version) => {
  const drivers = version?.drivers || [];
  const period = inv?.scope?.period || {};
  const month = period.from ? MONTHS[new Date(period.from).getUTCMonth()] : "the period";
  if (!drivers.length) return inv?.title || `Spend investigation · ${month}`;
  const net = netDelta(drivers);
  const top = [...drivers].sort((a, b) => Math.abs(b.deltaCost) - Math.abs(a.deltaCost))[0];
  const dir = net < 0 ? "decrease" : "increase";
  return `${top.label || top.key} usage drove a ${fmtSignedMoney(net)} spend ${dir} in ${month}`;
};

// deriveMateriality — heuristic band; analyst override (inv.materiality) wins upstream.
export const deriveMateriality = (net, runRate) => {
  const a = Math.abs(Number(net) || 0);
  const rr = Math.abs(Number(runRate) || 0);
  if (a >= 1000 || (rr > 0 && a >= 0.25 * rr)) return "high";
  if (a >= 250) return "medium";
  return "low";
};

// driverCause — plain-language cause from the price/volume split.
export const driverCause = (d = {}) => {
  const pv = d.priceVolume;
  if (Math.abs(Number(d.baselineCost) || 0) < 0.005 && (Number(d.deltaCost) || 0) > 0) return "New SKU";
  if (!pv) return "Mixed";
  const pe = Math.abs(pv.priceEffect || 0), ve = Math.abs(pv.volumeEffect || 0);
  if (pe === 0 && ve === 0) return "Mixed";
  if (pe >= ve * 2) return (pv.priceEffect || 0) >= 0 ? "Price increase" : "Price decrease";
  if (ve >= pe * 2) return (pv.volumeEffect || 0) >= 0 ? "Increased usage" : "Usage decrease";
  return "Mixed";
};

// primaryOwner — the organisation lens' largest-magnitude row (the "who owns it").
export const primaryOwner = (ownership = []) => {
  const org = (ownership || []).find((l) => l.dimensionCode === "organisation") || ownership[0];
  if (!org?.rows?.length) return null;
  const top = [...org.rows].sort((a, b) => Math.abs(b.deltaCost) - Math.abs(a.deltaCost))[0];
  return { label: top.label || `Owner #${top.ownerNodeId}`, deltaCost: top.deltaCost };
};

const UNRESOLVED = /shared services|unallocated|unassigned/i;
// unresolvedOwnership — the organisation lens' Shared/Unallocated row, if material.
export const unresolvedOwnership = (ownership = []) => {
  const org = (ownership || []).find((l) => l.dimensionCode === "organisation") || ownership[0];
  const row = (org?.rows || []).find((r) => UNRESOLVED.test(r.label || ""));
  if (!row || Math.abs(row.deltaCost) < 0.005) return null;
  return { label: row.label, deltaCost: row.deltaCost };
};

const STATUS_LABELS = { draft: "Draft", in_progress: "In review", needs_review: "Ready for decision", closed: "Closed" };
export const statusLabel = (s) => STATUS_LABELS[s] || "Draft";

// primaryActionForStatus — the workflow-dependent primary button {label, next}.
export const primaryActionForStatus = (s) => {
  switch (s) {
    case "draft": return { label: "Start review", next: "in_progress" };
    case "in_progress": return { label: "Mark ready for decision", next: "needs_review" };
    case "needs_review": return { label: "Close investigation", next: "closed" };
    case "closed": return { label: "Reopen", next: "in_progress" };
    default: return { label: "Start review", next: "in_progress" };
  }
};
